import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { useTheme } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Shield,
  Search,
  Edit,
  Trash2,
  Building2,
  FileText,
  Loader2,
  Plus,
  DollarSign,
  Calendar,
  Heart,
  Activity,
  Sparkles,
  Brain,
  Upload,
  ChevronDown,
  ChevronRight,
  Globe,
  MapPin,
  HeartPulse,
  CheckSquare,
  Square,
  X,
  RefreshCw,
  CheckCircle,
  FileSpreadsheet,
  ArrowRight,
  AlertCircle,
  Eye
} from "lucide-react";

export default function PlanesPage() {
  const { darkMode } = useTheme();
  const { getAuthHeaders } = useAuth();
  const [planes, setPlanes] = useState([]);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAseguradora, setFilterAseguradora] = useState("all");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [extractingAI, setExtractingAI] = useState(false);
  
  // Multi-selection state
  const [selectedPlans, setSelectedPlans] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [deletingMultiple, setDeletingMultiple] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  
  // State for expanded categories
  const [expandedCategories, setExpandedCategories] = useState({
    salud_internacional: true,
    salud_local: true,
    vida: true,
    accidentes: false,
    mixto: false,
    otros: false
  });

  // Sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  // Bulk import state (legacy - keeping for backwards compatibility)
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkStep, setBulkStep] = useState(1);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [asegMapping, setAsegMapping] = useState({});
  const [planesMapping, setPlanesMapping] = useState({});
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkDragging, setBulkDragging] = useState(false);
  const bulkFileInputRef = useCallback((node) => node, []);
  const fileInputRef = useCallback((node) => node, []);
  
  // Editable preview data state
  const [editableAseguradoras, setEditableAseguradoras] = useState([]);
  const [editablePlanes, setEditablePlanes] = useState([]);
  
  // NEW: Cargar Plan con IA state
  const [showCargarPlanDialog, setShowCargarPlanDialog] = useState(false);
  const [cargarPlanStep, setCargarPlanStep] = useState(1); // 1: Upload, 2: Preview/Edit, 3: Result
  const [cargarPlanLoading, setCargarPlanLoading] = useState(false);
  const [cargarPlanDragging, setCargarPlanDragging] = useState(false);
  const [extractedPlanData, setExtractedPlanData] = useState(null);
  const [selectedAseguradoraId, setSelectedAseguradoraId] = useState("");
  const [selectedCategoria, setSelectedCategoria] = useState("");
  const [cargarPlanResult, setCargarPlanResult] = useState(null);
  
  // Available fields for mapping
  const aseguradoraFields = [
    { value: "name", label: "Nombre" },
    { value: "description", label: "Descripción" },
    { value: "contact_email", label: "Email" },
    { value: "contact_phone", label: "Teléfono" },
    { value: "website", label: "Sitio Web" },
    { value: "categoria", label: "Categoría" }
  ];
  
  const planFields = [
    { value: "aseguradora_name", label: "Nombre Aseguradora" },
    { value: "name", label: "Nombre Plan" },
    { value: "description", label: "Descripción" },
    { value: "categoria", label: "Categoría" },
    { value: "tipo_plan", label: "Tipo Plan" },
    { value: "vigencia", label: "Vigencia" },
    { value: "moneda", label: "Moneda" },
    { value: "cobertura_hospitalizacion", label: "Cobertura Hospitalización" },
    { value: "cobertura_cirugia", label: "Cobertura Cirugía" },
    { value: "cobertura_maternidad", label: "Cobertura Maternidad" },
    { value: "cobertura_emergencia", label: "Cobertura Emergencia" },
    { value: "cobertura_medicamentos", label: "Cobertura Medicamentos" },
    { value: "suma_asegurada_maxima", label: "Suma Asegurada Máxima" },
    { value: "limite_anual", label: "Límite Anual" },
    { value: "copago_consulta", label: "Copago Consulta" },
    { value: "red_medica", label: "Red Médica" },
    { value: "beneficios_adicionales", label: "Beneficios Adicionales" }
  ];

  // Category definitions
  const CATEGORIES = [
    { 
      id: "salud_internacional", 
      label: "Salud Internacional", 
      icon: Globe, 
      color: "blue",
      description: "Planes con cobertura mundial"
    },
    { 
      id: "salud_local", 
      label: "Salud Local / ARS", 
      icon: MapPin, 
      color: "green",
      description: "Planes de cobertura en República Dominicana"
    },
    { 
      id: "vida", 
      label: "Vida", 
      icon: HeartPulse, 
      color: "purple",
      description: "Seguros de vida y protección familiar"
    },
    { 
      id: "accidentes", 
      label: "Accidentes", 
      icon: Activity, 
      color: "orange",
      description: "Cobertura de accidentes personales"
    },
    { 
      id: "mixto", 
      label: "Mixto", 
      icon: Shield, 
      color: "cyan",
      description: "Planes con múltiples coberturas"
    },
    { 
      id: "otros", 
      label: "Otros", 
      icon: FileText, 
      color: "slate",
      description: "Otros tipos de planes"
    }
  ];

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    tipo_plan: "",
    vigencia: "",
    moneda: "USD",
    cobertura_vida: "",
    cobertura_accidentes: "",
    cobertura_hospitalizacion: "",
    cobertura_cirugia: "",
    cobertura_maternidad: "",
    cobertura_dental: "",
    cobertura_vision: "",
    cobertura_medicamentos: "",
    cobertura_emergencia: "",
    cobertura_enfermedades_graves: "",
    suma_asegurada_maxima: "",
    limite_anual: "",
    deducible_general: "",
    copago_consulta: "",
    espera_general: "",
    espera_maternidad: "",
    prima_mensual: "",
    prima_anual: "",
    red_medica: "",
    exclusiones: "",
    beneficios_adicionales: "",
    notas_importantes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [asegRes] = await Promise.all([
        axios.get(`${API}/aseguradoras`, getAuthHeaders())
      ]);
      
      // Extract all planes from aseguradoras
      const allPlanes = [];
      asegRes.data.forEach(aseg => {
        (aseg.planes || []).forEach(plan => {
          allPlanes.push({
            ...plan,
            aseguradora_id: aseg.id,
            aseguradora_name: aseg.name
          });
        });
      });
      
      setAseguradoras(asegRes.data);
      setPlanes(allPlanes);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check sync status
  const checkSyncStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/aseguradoras/sincronizar/estado`, getAuthHeaders());
      setSyncStatus(res.data);
    } catch (err) {
      console.error("Error checking sync status:", err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    checkSyncStatus();
  }, [checkSyncStatus]);

  // Sincronizar aseguradoras y planes faltantes
  const handleSyncAseguradoras = async () => {
    try {
      setSyncing(true);
      const res = await axios.post(`${API}/aseguradoras/sincronizar`, {}, getAuthHeaders());
      toast.success(`${res.data.aseguradoras_creadas} aseguradoras y ${res.data.planes_creados} planes sincronizados`);
      fetchData();
      checkSyncStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  // Bulk Import Functions
  const openBulkDialog = () => {
    setBulkStep(1);
    setBulkPreview(null);
    setAsegMapping({});
    setPlanesMapping({});
    setBulkResult(null);
    setEditableAseguradoras([]);
    setEditablePlanes([]);
    setShowBulkDialog(true);
  };

  const handleBulkFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processBulkFile(file);
    if (e.target) e.target.value = '';
  };

  // Drag and drop handlers for bulk import
  const handleBulkDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBulkDragging(true);
  };

  const handleBulkDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBulkDragging(false);
  };

  const handleBulkDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBulkDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processBulkFile(files[0]);
    }
  };

  // Process the uploaded file for bulk import
  const processBulkFile = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedExtensions = ['.xlsx', '.xls', '.pdf', '.doc', '.docx'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      toast.error("Formato no soportado. Use Excel (.xlsx, .xls), PDF o Word (.docx)");
      return;
    }

    setBulkLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/aseguradoras/bulk-import/preview`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setBulkPreview(res.data);
      setAsegMapping(res.data.aseguradoras?.column_mapping || {});
      setPlanesMapping(res.data.planes?.column_mapping || {});
      
      // Initialize editable data from preview
      const asegData = res.data.aseguradoras?.all_data || [];
      const planesData = res.data.planes?.all_data || [];
      
      // Transform aseguradoras data to editable format
      setEditableAseguradoras(asegData.map((row, idx) => ({
        _idx: idx,
        nombre: row.nombre || row.name || '',
        descripcion: row.descripcion || row.description || '',
        useExisting: false,
        existingAseguradoraId: null,
        ...row
      })));
      
      // Transform planes data to editable format  
      setEditablePlanes(planesData.map((row, idx) => ({
        _idx: idx,
        nombre: row.nombre || row.name || row['Nombre Plan'] || '',
        aseguradora: row.aseguradora || row.aseguradora_name || '',
        descripcion: row.descripcion || row.description || '',
        useExistingAseguradora: false,
        existingAseguradoraId: null,
        ...row
      })));
      
      setBulkStep(2);
      toast.success(`${res.data.aseguradoras?.total_rows || 0} aseguradoras y ${res.data.planes?.total_rows || 0} planes encontrados. La IA ha mapeado los campos automáticamente.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar el archivo");
    } finally {
      setBulkLoading(false);
    }
  };

  const updateAsegMapping = (excelCol, field) => {
    setAsegMapping(prev => {
      const newMapping = { ...prev };
      if (field) {
        newMapping[excelCol] = field;
      } else {
        delete newMapping[excelCol];
      }
      return newMapping;
    });
  };

  const updatePlanesMapping = (excelCol, field) => {
    setPlanesMapping(prev => {
      const newMapping = { ...prev };
      if (field) {
        newMapping[excelCol] = field;
      } else {
        delete newMapping[excelCol];
      }
      return newMapping;
    });
  };

  // Handlers for editable preview
  const handleEditableAseguradoraChange = (idx, field, value) => {
    setEditableAseguradoras(prev => prev.map((item, i) => 
      i === idx ? { ...item, [field]: value } : item
    ));
  };

  const handleEditablePlanChange = (idx, field, value) => {
    setEditablePlanes(prev => prev.map((item, i) => 
      i === idx ? { ...item, [field]: value } : item
    ));
  };

  const handlePlanAseguradoraSelect = (planIdx, aseguradoraId) => {
    setEditablePlanes(prev => prev.map((item, i) => {
      if (i === planIdx) {
        if (aseguradoraId === "_new") {
          return { ...item, useExistingAseguradora: false, existingAseguradoraId: null };
        } else {
          const selectedAseg = aseguradoras.find(a => a.id === aseguradoraId);
          return { 
            ...item, 
            useExistingAseguradora: true, 
            existingAseguradoraId: aseguradoraId,
            aseguradora: selectedAseg?.name || item.aseguradora
          };
        }
      }
      return item;
    }));
  };

  const executeBulkImport = async () => {
    if (!bulkPreview) return;

    // Validate we have aseguradoras or planes with existing aseguradoras selected
    const hasNewAseguradoras = editableAseguradoras.length > 0;
    const allPlanesHaveAseguradora = editablePlanes.every(p => p.useExistingAseguradora || p.aseguradora);
    
    if (!hasNewAseguradoras && editablePlanes.length > 0 && !allPlanesHaveAseguradora) {
      toast.error("Cada plan debe tener una aseguradora asignada");
      return;
    }

    setBulkLoading(true);
    try {
      // Prepare aseguradoras data from editable state
      const aseguradorasToCreate = editableAseguradoras.filter(a => !a.useExisting).map(a => ({
        nombre: a.nombre,
        descripcion: a.descripcion,
        ...Object.fromEntries(Object.entries(a).filter(([k]) => !['_idx', 'useExisting', 'existingAseguradoraId'].includes(k)))
      }));
      
      // Prepare planes data from editable state
      const planesToCreate = editablePlanes.map(p => ({
        nombre: p.nombre,
        aseguradora: p.aseguradora,
        descripcion: p.descripcion,
        useExistingAseguradora: p.useExistingAseguradora,
        existingAseguradoraId: p.existingAseguradoraId,
        ...Object.fromEntries(Object.entries(p).filter(([k]) => !['_idx', 'useExistingAseguradora', 'existingAseguradoraId'].includes(k)))
      }));

      const res = await axios.post(`${API}/aseguradoras/bulk-import/execute`, {
        aseguradora_mapping: asegMapping,
        planes_mapping: planesMapping,
        default_categoria_aseguradora: bulkPreview.default_categoria_aseguradora || "local",
        default_categoria_plan: bulkPreview.default_categoria_plan || "salud_local",
        aseguradoras_rows: aseguradorasToCreate.length > 0 ? aseguradorasToCreate : bulkPreview.aseguradoras?.all_data || [],
        planes_rows: planesToCreate.length > 0 ? planesToCreate : bulkPreview.planes?.all_data || [],
        editable_preview: true
      }, getAuthHeaders());

      setBulkResult(res.data);
      setBulkStep(3);
      
      if (res.data.aseguradoras_created > 0 || res.data.planes_created > 0) {
        toast.success(`${res.data.aseguradoras_created} aseguradoras y ${res.data.planes_created} planes importados`);
        fetchData();
        checkSyncStatus();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name || "",
      description: plan.description || "",
      tipo_plan: plan.tipo_plan || "",
      vigencia: plan.vigencia || "",
      moneda: plan.moneda || "USD",
      cobertura_vida: plan.cobertura_vida || "",
      cobertura_accidentes: plan.cobertura_accidentes || "",
      cobertura_hospitalizacion: plan.cobertura_hospitalizacion || "",
      cobertura_cirugia: plan.cobertura_cirugia || "",
      cobertura_maternidad: plan.cobertura_maternidad || "",
      cobertura_dental: plan.cobertura_dental || "",
      cobertura_vision: plan.cobertura_vision || "",
      cobertura_medicamentos: plan.cobertura_medicamentos || "",
      cobertura_emergencia: plan.cobertura_emergencia || "",
      cobertura_enfermedades_graves: plan.cobertura_enfermedades_graves || "",
      suma_asegurada_maxima: plan.suma_asegurada_maxima || "",
      limite_anual: plan.limite_anual || "",
      deducible_general: plan.deducible_general || "",
      copago_consulta: plan.copago_consulta || "",
      espera_general: plan.espera_general || "",
      espera_maternidad: plan.espera_maternidad || "",
      prima_mensual: plan.prima_mensual || "",
      prima_anual: plan.prima_anual || "",
      red_medica: plan.red_medica || "",
      exclusiones: plan.exclusiones || "",
      beneficios_adicionales: plan.beneficios_adicionales || "",
      notas_importantes: plan.notas_importantes || ""
    });
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    setSaving(true);
    try {
      const updateData = new FormData();
      updateData.append("aseguradora_id", editingPlan.aseguradora_id);
      Object.keys(formData).forEach(key => {
        updateData.append(key, formData[key]);
      });

      await axios.put(
        `${API}/planes-cobertura/${editingPlan.id}`,
        updateData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success("Plan actualizado correctamente");
      setShowEditDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar plan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`¿Está seguro de eliminar el plan "${plan.name}"?`)) return;

    try {
      await axios.delete(`${API}/planes-cobertura/${plan.id}`, getAuthHeaders());
      toast.success("Plan eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar plan");
    }
  };

  // Multi-selection functions
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedPlans(new Set());
  };

  const togglePlanSelection = (id) => {
    const newSelected = new Set(selectedPlans);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedPlans(newSelected);
  };

  const selectAllPlans = () => {
    const allIds = new Set(filteredPlanes.map(p => p.id));
    setSelectedPlans(allIds);
  };

  const deselectAllPlans = () => {
    setSelectedPlans(new Set());
  };

  const handleDeleteSelectedPlans = async () => {
    if (selectedPlans.size === 0) return;
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteSelectedPlans = async () => {
    setDeletingMultiple(true);
    let successCount = 0;
    let errorCount = 0;

    for (const id of selectedPlans) {
      try {
        await axios.delete(`${API}/planes-cobertura/${id}`, getAuthHeaders());
        successCount++;
      } catch (error) {
        console.error(`Error deleting plan ${id}:`, error);
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} plan(es) eliminado(s) exitosamente`);
      fetchData();
    }
    if (errorCount > 0) {
      toast.error(`No se pudieron eliminar ${errorCount} plan(es)`);
    }

    setSelectedPlans(new Set());
    setIsSelectionMode(false);
    setDeletingMultiple(false);
    setShowDeleteConfirmDialog(false);
  };

  const selectAllInCategory = (categoryId) => {
    const categoryPlans = groupedPlanes[categoryId] || [];
    const newSelected = new Set(selectedPlans);
    categoryPlans.forEach(p => newSelected.add(p.id));
    setSelectedPlans(newSelected);
  };

  const handleExtractFromDocument = async (file) => {
    if (!file) return;
    
    setExtractingAI(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      
      const response = await axios.post(
        `${API}/planes-cobertura/extract-from-document`,
        uploadData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success && response.data.extracted_data) {
        const data = response.data.extracted_data;
        setFormData(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(data).filter(([_, v]) => v !== "" && v !== null)
          )
        }));
        toast.success("Datos extraídos con IA correctamente");
      } else {
        toast.error(response.data.message || "Error al extraer datos");
      }
    } catch (err) {
      toast.error("Error al procesar documento");
    } finally {
      setExtractingAI(false);
    }
  };

  // ========== CARGAR PLAN CON IA FUNCTIONS ==========
  
  const openCargarPlanDialog = () => {
    setCargarPlanStep(1);
    setExtractedPlanData(null);
    setSelectedAseguradoraId("");
    setSelectedCategoria("");
    setCargarPlanResult(null);
    setShowCargarPlanDialog(true);
  };

  const handleCargarPlanDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCargarPlanDragging(true);
  };

  const handleCargarPlanDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCargarPlanDragging(false);
  };

  const handleCargarPlanDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCargarPlanDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processCargarPlanFile(files[0]);
    }
  };

  const handleCargarPlanFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await processCargarPlanFile(file);
    }
    if (e.target) e.target.value = '';
  };

  const processCargarPlanFile = async (file) => {
    const allowedExtensions = ['.pdf', '.docx', '.doc'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      toast.error("Solo se permiten archivos PDF o Word (.pdf, .docx, .doc)");
      return;
    }

    setCargarPlanLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/planes-cobertura/cargar-plan-ai`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res.data.success) {
        setExtractedPlanData({
          ...res.data.plan_data,
          aseguradora_detectada: res.data.aseguradora_detectada,
          aseguradora_match: res.data.aseguradora_match,
          aseguradoras_disponibles: res.data.aseguradoras_disponibles || aseguradoras
        });
        
        // Auto-select aseguradora if matched
        if (res.data.aseguradora_match) {
          setSelectedAseguradoraId(res.data.aseguradora_match.id);
        }
        
        // Auto-select categoria
        if (res.data.categoria_detectada) {
          setSelectedCategoria(res.data.categoria_detectada);
        }
        
        setCargarPlanStep(2);
        toast.success("Documento analizado correctamente. Revise y edite los datos antes de confirmar.");
      } else {
        toast.error(res.data.message || "Error al procesar el documento");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar el documento");
    } finally {
      setCargarPlanLoading(false);
    }
  };

  const handleExtractedPlanChange = (field, value) => {
    setExtractedPlanData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const confirmarCargaPlan = async () => {
    if (!selectedAseguradoraId) {
      toast.error("Debe seleccionar una aseguradora");
      return;
    }
    if (!extractedPlanData?.name) {
      toast.error("El nombre del plan es requerido");
      return;
    }

    setCargarPlanLoading(true);
    try {
      const res = await axios.post(`${API}/planes-cobertura/confirmar-carga`, {
        aseguradora_id: selectedAseguradoraId,
        categoria: selectedCategoria || "salud_local",
        plan_data: extractedPlanData
      }, getAuthHeaders());

      if (res.data.success) {
        setCargarPlanResult(res.data);
        setCargarPlanStep(3);
        toast.success(res.data.message);
        fetchData(); // Refresh plans list
      } else {
        toast.error(res.data.message || "Error al guardar el plan");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el plan");
    } finally {
      setCargarPlanLoading(false);
    }
  };

  // ========== END CARGAR PLAN FUNCTIONS ==========

  const filteredPlanes = planes.filter(plan => {
    const matchesSearch = 
      plan.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plan.aseguradora_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plan.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAseg = filterAseguradora === "all" || plan.aseguradora_id === filterAseguradora;
    return matchesSearch && matchesAseg;
  });

  // Group planes by category
  const groupedPlanes = CATEGORIES.reduce((acc, category) => {
    acc[category.id] = filteredPlanes.filter(plan => {
      const planCategory = plan.categoria || "otros";
      if (category.id === "otros") {
        return !["salud_internacional", "salud_local", "vida", "accidentes", "mixto"].includes(planCategory);
      }
      return planCategory === category.id;
    });
    return acc;
  }, {});

  // Get color classes for each category
  const getCategoryColors = (color) => {
    const colors = {
      blue: { bg: "bg-blue-500/10", text: "text-blue-500", border: "border-blue-500/30", badge: "bg-blue-500" },
      green: { bg: "bg-green-500/10", text: "text-green-500", border: "border-green-500/30", badge: "bg-green-500" },
      purple: { bg: "bg-purple-500/10", text: "text-purple-500", border: "border-purple-500/30", badge: "bg-purple-500" },
      orange: { bg: "bg-orange-500/10", text: "text-orange-500", border: "border-orange-500/30", badge: "bg-orange-500" },
      cyan: { bg: "bg-cyan-500/10", text: "text-cyan-500", border: "border-cyan-500/30", badge: "bg-cyan-500" },
      slate: { bg: "bg-slate-500/10", text: "text-slate-500", border: "border-slate-500/30", badge: "bg-slate-500" }
    };
    return colors[color] || colors.slate;
  };

  const bgClass = darkMode ? "bg-gray-900" : "bg-slate-50";
  const cardClass = darkMode ? "bg-gray-800 border-gray-700" : "bg-white";
  const textClass = darkMode ? "text-white" : "text-slate-900";
  const mutedClass = darkMode ? "text-gray-400" : "text-slate-600";

  return (
    <div className={`min-h-screen flex ${bgClass}`}>
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className={`text-3xl font-bold ${textClass}`}>
                Planes de Cobertura
              </h1>
              <p className={mutedClass}>
                Gestiona todos los planes de cobertura de tus aseguradoras
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Multi-selection controls */}
              {isSelectionMode ? (
                <>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-slate-100'}`}>
                    <span className={`text-sm ${mutedClass}`}>
                      {selectedPlans.size} seleccionados
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllPlans}
                    className={darkMode ? "border-gray-600 text-gray-300" : ""}
                    data-testid="select-all-plans-button"
                  >
                    <CheckSquare className="w-4 h-4 mr-1" />
                    Todos
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={deselectAllPlans}
                    className={darkMode ? "border-gray-600 text-gray-300" : ""}
                    data-testid="deselect-all-plans-button"
                  >
                    <Square className="w-4 h-4 mr-1" />
                    Ninguno
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDeleteSelectedPlans}
                    disabled={selectedPlans.size === 0 || deletingMultiple}
                    data-testid="delete-selected-plans-button"
                  >
                    {deletingMultiple ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-1" />
                    )}
                    Eliminar ({selectedPlans.size})
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleSelectionMode}
                    data-testid="cancel-plan-selection-button"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  {/* Sync button */}
                  {syncStatus && syncStatus.faltantes > 0 && (
                    <Button 
                      variant="outline"
                      onClick={handleSyncAseguradoras}
                      disabled={syncing}
                      className="border-green-500 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                      data-testid="sync-planes-button"
                    >
                      {syncing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Sincronizar ({syncStatus.faltantes})
                    </Button>
                  )}
                  
                  {/* Sync badge */}
                  {syncStatus && syncStatus.faltantes === 0 && planes.length > 0 && (
                    <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200 py-2 px-3 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Sincronizado
                    </Badge>
                  )}
                  
                  {/* Cargar Plan button - NEW */}
                  <Button 
                    onClick={openCargarPlanDialog}
                    className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                    data-testid="cargar-plan-button"
                  >
                    <Brain className="w-4 h-4 mr-2" />
                    Cargar Plan
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={toggleSelectionMode}
                    className={darkMode ? "border-gray-600 text-gray-300" : ""}
                    data-testid="enable-plan-selection-button"
                  >
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Seleccionar
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className={cardClass}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-500/10 rounded-lg">
                    <Shield className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <p className={`text-sm ${mutedClass}`}>Total Planes</p>
                    <p className={`text-2xl font-bold ${textClass}`}>{planes.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={cardClass}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <Building2 className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <p className={`text-sm ${mutedClass}`}>Aseguradoras</p>
                    <p className={`text-2xl font-bold ${textClass}`}>{aseguradoras.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={cardClass}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-500/10 rounded-lg">
                    <Heart className="h-6 w-6 text-purple-500" />
                  </div>
                  <div>
                    <p className={`text-sm ${mutedClass}`}>Con Maternidad</p>
                    <p className={`text-2xl font-bold ${textClass}`}>
                      {planes.filter(p => p.cobertura_maternidad).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={cardClass}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-500/10 rounded-lg">
                    <Activity className="h-6 w-6 text-orange-500" />
                  </div>
                  <div>
                    <p className={`text-sm ${mutedClass}`}>Resultados</p>
                    <p className={`text-2xl font-bold ${textClass}`}>{filteredPlanes.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className={`mb-6 ${cardClass}`}>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${mutedClass}`} />
                  <Input
                    placeholder="Buscar por nombre, aseguradora..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`pl-10 ${darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}`}
                  />
                </div>
                <Select value={filterAseguradora} onValueChange={setFilterAseguradora}>
                  <SelectTrigger className={`w-[200px] ${darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}`}>
                    <SelectValue placeholder="Filtrar aseguradora" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las aseguradoras</SelectItem>
                    {aseguradoras.map(aseg => (
                      <SelectItem key={aseg.id} value={aseg.id}>{aseg.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Planes Grouped by Category */}
          <div className="space-y-4">
            {loading ? (
              <Card className={cardClass}>
                <CardContent className="py-12">
                  <div className="flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  </div>
                </CardContent>
              </Card>
            ) : filteredPlanes.length === 0 ? (
              <Card className={cardClass}>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Shield className={`h-12 w-12 mx-auto mb-4 ${mutedClass}`} />
                    <p className={mutedClass}>No hay planes de cobertura</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              CATEGORIES.map(category => {
                const categoryPlanes = groupedPlanes[category.id] || [];
                if (categoryPlanes.length === 0) return null;
                
                const colors = getCategoryColors(category.color);
                const IconComponent = category.icon;
                const isExpanded = expandedCategories[category.id];

                return (
                  <Card key={category.id} className={`${cardClass} overflow-hidden`}>
                    <Collapsible open={isExpanded} onOpenChange={() => toggleCategory(category.id)}>
                      <CollapsibleTrigger className="w-full" data-testid={`category-${category.id}`}>
                        <div className={`flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700/50 transition-colors ${colors.border} border-l-4`}>
                          <div className="flex items-center gap-4">
                            <div className={`p-3 ${colors.bg} rounded-lg`}>
                              <IconComponent className={`h-6 w-6 ${colors.text}`} />
                            </div>
                            <div className="text-left">
                              <h3 className={`text-lg font-semibold ${textClass}`}>
                                {category.label}
                              </h3>
                              <p className={`text-sm ${mutedClass}`}>
                                {category.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={`${colors.badge} text-white`}>
                              {categoryPlanes.length} {categoryPlanes.length === 1 ? 'plan' : 'planes'}
                            </Badge>
                            {isExpanded ? (
                              <ChevronDown className={`h-5 w-5 ${mutedClass}`} />
                            ) : (
                              <ChevronRight className={`h-5 w-5 ${mutedClass}`} />
                            )}
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className={`border-t ${darkMode ? "border-gray-700" : "border-slate-200"}`}>
                          {/* Category selection controls */}
                          {isSelectionMode && categoryPlanes.length > 0 && (
                            <div className={`flex items-center gap-2 px-4 py-2 ${darkMode ? 'bg-gray-700/50' : 'bg-slate-50'}`}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => selectAllInCategory(category.id)}
                                className={`text-xs ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}
                              >
                                <CheckSquare className="w-3 h-3 mr-1" />
                                Seleccionar todos en {category.label}
                              </Button>
                            </div>
                          )}
                          <Table>
                            <TableHeader>
                              <TableRow className={darkMode ? "border-gray-700" : ""}>
                                {isSelectionMode && <TableHead className="w-12"></TableHead>}
                                <TableHead className={mutedClass}>Plan</TableHead>
                                <TableHead className={mutedClass}>Aseguradora</TableHead>
                                <TableHead className={mutedClass}>Tipo</TableHead>
                                <TableHead className={mutedClass}>Prima</TableHead>
                                <TableHead className={mutedClass}>Coberturas</TableHead>
                                <TableHead className={`text-right ${mutedClass}`}>Acciones</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {categoryPlanes.map((plan) => (
                                <TableRow 
                                  key={plan.id} 
                                  className={`${darkMode ? "border-gray-700" : ""} ${
                                    isSelectionMode && selectedPlans.has(plan.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                  }`}
                                >
                                  {isSelectionMode && (
                                    <TableCell className="w-12">
                                      <Checkbox
                                        checked={selectedPlans.has(plan.id)}
                                        onCheckedChange={() => togglePlanSelection(plan.id)}
                                        className="w-4 h-4"
                                        data-testid={`plan-checkbox-${plan.id}`}
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell>
                                    <div className="flex items-center gap-3">
                                      <div className={`p-2 ${colors.bg} rounded-lg`}>
                                        <Shield className={`h-5 w-5 ${colors.text}`} />
                                      </div>
                                      <div>
                                        <p className={`font-medium ${textClass}`}>{plan.name}</p>
                                        <p className={`text-xs ${mutedClass}`}>
                                          {plan.description?.slice(0, 40)}{plan.description?.length > 40 ? '...' : ''}
                                        </p>
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className={darkMode ? "border-gray-600 text-gray-300" : ""}>
                                      {plan.aseguradora_name}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className={mutedClass}>
                                    {plan.tipo_plan || "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1">
                                      <DollarSign className="h-4 w-4 text-green-500" />
                                      <span className={textClass}>
                                        {plan.prima_mensual || plan.premium_range || "N/A"}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-1 flex-wrap">
                                      {plan.cobertura_vida && <Badge className="bg-blue-500/20 text-blue-600 text-xs">Vida</Badge>}
                                      {plan.cobertura_hospitalizacion && <Badge className="bg-purple-500/20 text-purple-600 text-xs">Hosp</Badge>}
                                      {plan.cobertura_maternidad && <Badge className="bg-pink-500/20 text-pink-600 text-xs">Mat</Badge>}
                                      {plan.cobertura_dental && <Badge className="bg-cyan-500/20 text-cyan-600 text-xs">Dental</Badge>}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEdit(plan)}
                                      className={darkMode ? "text-gray-300 hover:text-white" : ""}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDelete(plan)}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </Card>
                );
              })
            )}
          </div>
        </div>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className={`max-w-4xl max-h-[90vh] ${darkMode ? "bg-gray-800 border-gray-700" : ""}`}>
            <DialogHeader>
              <DialogTitle className={textClass}>
                Editar Plan: {editingPlan?.name}
              </DialogTitle>
            </DialogHeader>

            {/* AI Extraction */}
            <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <Brain className="w-6 h-6 text-purple-600" />
              <div className="flex-1">
                <p className={`font-medium ${textClass}`}>Actualizar con IA</p>
                <p className={`text-sm ${mutedClass}`}>Sube un documento para extraer datos automáticamente</p>
              </div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.docx,.doc,.xlsx,.xls"
                  onChange={(e) => handleExtractFromDocument(e.target.files[0])}
                  className="hidden"
                />
                <Button disabled={extractingAI} className="bg-purple-600 hover:bg-purple-700">
                  {extractingAI ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {extractingAI ? "Extrayendo..." : "Cargar Documento"}
                </Button>
              </label>
            </div>

            <ScrollArea className="h-[50vh] pr-4">
              <div className="space-y-6 py-4">
                {/* Información básica */}
                <div className="space-y-4">
                  <h3 className={`font-semibold border-b pb-2 ${textClass}`}>Información Básica</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className={mutedClass}>Nombre del Plan *</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                    </div>
                    <div className="space-y-2">
                      <Label className={mutedClass}>Tipo de Plan</Label>
                      <Input value={formData.tipo_plan} onChange={(e) => setFormData({...formData, tipo_plan: e.target.value})} placeholder="Individual/Familiar/Colectivo" className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className={mutedClass}>Descripción</Label>
                    <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                  </div>
                </div>

                {/* Coberturas */}
                <div className="space-y-4">
                  <h3 className={`font-semibold border-b pb-2 ${textClass}`}>Coberturas Principales</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className={mutedClass}>Vida</Label>
                      <Input value={formData.cobertura_vida} onChange={(e) => setFormData({...formData, cobertura_vida: e.target.value})} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                    </div>
                    <div className="space-y-2">
                      <Label className={mutedClass}>Hospitalización</Label>
                      <Input value={formData.cobertura_hospitalizacion} onChange={(e) => setFormData({...formData, cobertura_hospitalizacion: e.target.value})} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                    </div>
                    <div className="space-y-2">
                      <Label className={mutedClass}>Cirugía</Label>
                      <Input value={formData.cobertura_cirugia} onChange={(e) => setFormData({...formData, cobertura_cirugia: e.target.value})} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                    </div>
                    <div className="space-y-2">
                      <Label className={mutedClass}>Maternidad</Label>
                      <Input value={formData.cobertura_maternidad} onChange={(e) => setFormData({...formData, cobertura_maternidad: e.target.value})} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                    </div>
                    <div className="space-y-2">
                      <Label className={mutedClass}>Emergencia</Label>
                      <Input value={formData.cobertura_emergencia} onChange={(e) => setFormData({...formData, cobertura_emergencia: e.target.value})} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                    </div>
                    <div className="space-y-2">
                      <Label className={mutedClass}>Enfermedades Graves</Label>
                      <Input value={formData.cobertura_enfermedades_graves} onChange={(e) => setFormData({...formData, cobertura_enfermedades_graves: e.target.value})} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                    </div>
                  </div>
                </div>

                {/* Límites y Primas */}
                <div className="space-y-4">
                  <h3 className={`font-semibold border-b pb-2 ${textClass}`}>Límites, Deducibles y Primas</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className={mutedClass}>Suma Asegurada Máxima</Label>
                      <Input value={formData.suma_asegurada_maxima} onChange={(e) => setFormData({...formData, suma_asegurada_maxima: e.target.value})} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                    </div>
                    <div className="space-y-2">
                      <Label className={mutedClass}>Deducible General</Label>
                      <Input value={formData.deducible_general} onChange={(e) => setFormData({...formData, deducible_general: e.target.value})} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                    </div>
                    <div className="space-y-2">
                      <Label className={mutedClass}>Prima Mensual</Label>
                      <Input value={formData.prima_mensual} onChange={(e) => setFormData({...formData, prima_mensual: e.target.value})} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                    </div>
                    <div className="space-y-2">
                      <Label className={mutedClass}>Prima Anual</Label>
                      <Input value={formData.prima_anual} onChange={(e) => setFormData({...formData, prima_anual: e.target.value})} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                    </div>
                  </div>
                </div>

                {/* Otros */}
                <div className="space-y-4">
                  <h3 className={`font-semibold border-b pb-2 ${textClass}`}>Información Adicional</h3>
                  <div className="space-y-2">
                    <Label className={mutedClass}>Red Médica</Label>
                    <Input value={formData.red_medica} onChange={(e) => setFormData({...formData, red_medica: e.target.value})} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                  </div>
                  <div className="space-y-2">
                    <Label className={mutedClass}>Exclusiones</Label>
                    <Textarea value={formData.exclusiones} onChange={(e) => setFormData({...formData, exclusiones: e.target.value})} rows={2} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                  </div>
                  <div className="space-y-2">
                    <Label className={mutedClass}>Beneficios Adicionales</Label>
                    <Textarea value={formData.beneficios_adicionales} onChange={(e) => setFormData({...formData, beneficios_adicionales: e.target.value})} rows={2} className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} />
                  </div>
                </div>
              </div>
            </ScrollArea>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)} className={darkMode ? "border-gray-600" : ""}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Guardar Cambios
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
          <DialogContent className={`max-w-md ${darkMode ? "bg-gray-800 border-gray-700" : ""}`}>
            <DialogHeader>
              <DialogTitle className={textClass}>
                Confirmar eliminación
              </DialogTitle>
              <DialogDescription className={mutedClass}>
                ¿Estás seguro de que deseas eliminar {selectedPlans.size} plan(es)? 
                Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirmDialog(false)}
                disabled={deletingMultiple}
                className={darkMode ? "border-gray-600" : ""}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteSelectedPlans}
                disabled={deletingMultiple}
                data-testid="confirm-delete-plans-button"
              >
                {deletingMultiple ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar {selectedPlans.size}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Dialog */}
        <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
          <DialogContent className={`max-w-4xl max-h-[85vh] flex flex-col ${darkMode ? "bg-gray-800 border-gray-700" : ""}`}>
            <DialogHeader>
              <DialogTitle className={`text-xl font-bold font-['Manrope'] flex items-center gap-2 ${textClass}`}>
                <FileSpreadsheet className="w-5 h-5 text-[#0C314C]" />
                Importar Aseguradoras y Planes
              </DialogTitle>
              <DialogDescription className={mutedClass}>
                {bulkStep === 1 && "Sube un archivo Excel, PDF o Word con los datos de aseguradoras y planes"}
                {bulkStep === 2 && "Verifica el mapeo de columnas generado por la IA"}
                {bulkStep === 3 && "Resultado de la importación"}
              </DialogDescription>
            </DialogHeader>

            {/* Progress Steps */}
            <div className={`flex items-center justify-center gap-2 py-4 border-b ${darkMode ? "border-gray-700" : ""}`}>
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 1 ? 'bg-[#0C314C] text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">1</span>
                Subir
              </div>
              <ArrowRight className={`w-4 h-4 ${mutedClass}`} />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 2 ? 'bg-[#0C314C] text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">2</span>
                Mapear
              </div>
              <ArrowRight className={`w-4 h-4 ${mutedClass}`} />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 3 ? 'bg-[#0C314C] text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">3</span>
                Resultado
              </div>
            </div>

            <div className="flex-1 overflow-auto py-4">
              {/* Step 1: Upload */}
              {bulkStep === 1 && (
                <div 
                  className={`flex flex-col items-center justify-center py-8 mx-4 border-2 border-dashed rounded-xl transition-all duration-200 ${
                    bulkDragging 
                      ? 'border-[#0C314C] bg-[#0C314C]/10 scale-[1.02]' 
                      : darkMode 
                        ? 'border-gray-600 hover:border-[#0C314C]' 
                        : 'border-slate-300 hover:border-[#0C314C]'
                  }`}
                  onDragOver={handleBulkDragOver}
                  onDragLeave={handleBulkDragLeave}
                  onDrop={handleBulkDrop}
                >
                  <input
                    type="file"
                    accept=".xlsx,.xls,.pdf,.docx,.doc"
                    onChange={handleBulkFileUpload}
                    className="hidden"
                    id="bulk-import-file"
                  />
                  
                  {bulkLoading ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-12 h-12 animate-spin text-[#0C314C] mx-auto mb-4" />
                      <p className={`text-lg font-medium ${textClass}`}>Analizando archivo con IA...</p>
                      <p className={`text-sm ${mutedClass} mt-2`}>Esto puede tomar unos segundos</p>
                    </div>
                  ) : bulkDragging ? (
                    <div className="text-center py-8">
                      <div className="w-20 h-20 rounded-full bg-[#0C314C]/20 flex items-center justify-center mb-4 mx-auto animate-pulse">
                        <Upload className="w-10 h-10 text-[#0C314C]" />
                      </div>
                      <p className="text-lg font-medium text-[#0C314C]">Suelte el archivo aquí</p>
                    </div>
                  ) : (
                    <>
                      <div className="w-20 h-20 rounded-full bg-[#0C314C]/10 flex items-center justify-center mb-4">
                        <FileSpreadsheet className="w-10 h-10 text-[#0C314C]" />
                      </div>
                      
                      <p className={`text-lg font-medium mb-2 ${textClass}`}>
                        Arrastra y suelta tu archivo aquí
                      </p>
                      <p className={`text-sm ${mutedClass} mb-4`}>o</p>
                      
                      <label htmlFor="bulk-import-file">
                        <Button 
                          className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white mb-6 cursor-pointer"
                          asChild
                        >
                          <span>
                            <Upload className="w-4 h-4 mr-2" />
                            Seleccionar Archivo
                          </span>
                        </Button>
                      </label>
                      
                      <div className={`text-sm ${mutedClass} text-center max-w-lg space-y-2`}>
                        <div className={`${darkMode ? 'bg-gray-700' : 'bg-slate-50'} rounded-lg p-3 text-left text-xs`}>
                          <p className="font-semibold mb-2">Formatos soportados:</p>
                          <p>• <strong>Excel (.xlsx, .xls):</strong> Datos estructurados en tablas</p>
                          <p>• <strong>PDF (.pdf):</strong> Documentos de pólizas y coberturas</p>
                          <p>• <strong>Word (.docx, .doc):</strong> Documentos de contratos</p>
                        </div>
                        <div className={`${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} rounded-lg p-3 text-left text-xs mt-3`}>
                          <p className="font-semibold mb-1 text-blue-700 dark:text-blue-300">Tip:</p>
                          <p className="text-blue-600 dark:text-blue-400">Para PDF/Word, la IA extraerá información de aseguradoras y planes automáticamente.</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Step 2: Mapping */}
              {bulkStep === 2 && bulkPreview && (
                <ScrollArea className="h-[55vh]">
                  <div className="space-y-6 pr-4">
                    {/* Aseguradoras Mapping */}
                    <div>
                      <h3 className={`font-semibold mb-3 ${textClass} flex items-center gap-2`}>
                        <Building2 className="w-4 h-4 text-[#0C314C]" />
                        Mapeo de Aseguradoras ({bulkPreview.aseguradoras?.total_rows || 0} filas)
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {bulkPreview.aseguradoras?.headers?.map(header => (
                          <div key={`aseg-${header}`} className={`flex items-center gap-2 p-2 ${darkMode ? 'bg-gray-700' : 'bg-slate-50'} rounded-lg`}>
                            <span className={`text-sm ${mutedClass} flex-1 truncate`} title={header}>
                              {header}
                            </span>
                            <ArrowRight className={`w-4 h-4 ${mutedClass} flex-shrink-0`} />
                            <Select 
                              value={asegMapping[header] || "_none"} 
                              onValueChange={(value) => updateAsegMapping(header, value === "_none" ? "" : value)}
                            >
                              <SelectTrigger className={`w-36 text-xs h-8 ${darkMode ? 'bg-gray-600 border-gray-500' : ''}`}>
                                <SelectValue placeholder="No mapear" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">No mapear</SelectItem>
                                {aseguradoraFields.map(field => (
                                  <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Planes Mapping (if available) */}
                    {bulkPreview.planes && bulkPreview.planes.headers?.length > 0 && (
                      <div>
                        <h3 className={`font-semibold mb-3 ${textClass} flex items-center gap-2`}>
                          <Shield className="w-4 h-4 text-[#45E69A]" />
                          Mapeo de Planes ({bulkPreview.planes?.total_rows || 0} filas)
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                          {bulkPreview.planes.headers.map(header => (
                            <div key={`plan-${header}`} className={`flex items-center gap-2 p-2 ${darkMode ? 'bg-gray-700' : 'bg-slate-50'} rounded-lg`}>
                              <span className={`text-sm ${mutedClass} flex-1 truncate`} title={header}>
                                {header}
                              </span>
                              <ArrowRight className={`w-4 h-4 ${mutedClass} flex-shrink-0`} />
                              <Select 
                                value={planesMapping[header] || "_none"} 
                                onValueChange={(value) => updatePlanesMapping(header, value === "_none" ? "" : value)}
                              >
                                <SelectTrigger className={`w-40 text-xs h-8 ${darkMode ? 'bg-gray-600 border-gray-500' : ''}`}>
                                  <SelectValue placeholder="No mapear" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">No mapear</SelectItem>
                                  {planFields.map(field => (
                                    <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Defaults */}
                    <div className={`flex gap-4 p-3 ${darkMode ? 'bg-amber-900/20' : 'bg-amber-50'} rounded-lg`}>
                      <div className="flex-1">
                        <Label className={`text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>Categoría aseguradora por defecto</Label>
                        <Select 
                          value={bulkPreview.default_categoria_aseguradora || "local"} 
                          onValueChange={(value) => setBulkPreview({...bulkPreview, default_categoria_aseguradora: value})}
                        >
                          <SelectTrigger className={`h-8 text-sm mt-1 ${darkMode ? 'bg-gray-700 border-gray-600' : ''}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="internacional">Internacional</SelectItem>
                            <SelectItem value="local">Local/ARS</SelectItem>
                            <SelectItem value="vida">Vida</SelectItem>
                            <SelectItem value="multi_ramo">Multi-Ramo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {bulkPreview.planes && (
                        <div className="flex-1">
                          <Label className={`text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>Categoría plan por defecto</Label>
                          <Select 
                            value={bulkPreview.default_categoria_plan || "salud_local"} 
                            onValueChange={(value) => setBulkPreview({...bulkPreview, default_categoria_plan: value})}
                          >
                            <SelectTrigger className={`h-8 text-sm mt-1 ${darkMode ? 'bg-gray-700 border-gray-600' : ''}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="salud_internacional">Salud Internacional</SelectItem>
                              <SelectItem value="salud_local">Salud Local</SelectItem>
                              <SelectItem value="vida">Vida</SelectItem>
                              <SelectItem value="accidentes">Accidentes</SelectItem>
                              <SelectItem value="mixto">Mixto</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Data Preview Section - EDITABLE */}
                    <div className={`mt-6 border-t ${darkMode ? 'border-gray-700' : 'border-slate-200'} pt-4`}>
                      <h3 className={`font-semibold mb-3 ${textClass} flex items-center gap-2`}>
                        <Eye className="w-4 h-4 text-[#0C314C]" />
                        Vista Previa de Datos a Importar
                        <Badge variant="outline" className="ml-2 text-xs bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                          Editable
                        </Badge>
                      </h3>
                      
                      {/* Editable Aseguradoras Preview */}
                      {editableAseguradoras.length > 0 && (
                        <div className="mb-4">
                          <p className={`text-sm font-medium mb-2 ${mutedClass}`}>
                            Aseguradoras ({editableAseguradoras.length})
                          </p>
                          <div className={`overflow-auto max-h-52 rounded-lg border ${darkMode ? 'border-gray-700' : 'border-slate-200'}`}>
                            <table className="w-full text-sm">
                              <thead className={`sticky top-0 ${darkMode ? 'bg-gray-700' : 'bg-slate-100'}`}>
                                <tr>
                                  <th className={`text-left p-2 ${textClass} w-12`}>#</th>
                                  <th className={`text-left p-2 ${textClass}`}>Nombre</th>
                                  <th className={`text-left p-2 ${textClass}`}>Descripción</th>
                                </tr>
                              </thead>
                              <tbody>
                                {editableAseguradoras.map((aseg, idx) => (
                                  <tr key={idx} className={`border-t ${darkMode ? 'border-gray-700' : 'border-slate-200'}`}>
                                    <td className={`p-2 ${mutedClass}`}>{idx + 1}</td>
                                    <td className="p-1">
                                      <Input
                                        value={aseg.nombre}
                                        onChange={(e) => handleEditableAseguradoraChange(idx, 'nombre', e.target.value)}
                                        className={`h-8 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                                        placeholder="Nombre de aseguradora"
                                        data-testid={`aseg-name-input-${idx}`}
                                      />
                                    </td>
                                    <td className="p-1">
                                      <Input
                                        value={aseg.descripcion}
                                        onChange={(e) => handleEditableAseguradoraChange(idx, 'descripcion', e.target.value)}
                                        className={`h-8 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                                        placeholder="Descripción"
                                        data-testid={`aseg-desc-input-${idx}`}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Editable Planes Preview */}
                      {editablePlanes.length > 0 && (
                        <div>
                          <p className={`text-sm font-medium mb-2 ${mutedClass}`}>
                            Planes ({editablePlanes.length})
                          </p>
                          <div className={`overflow-auto max-h-64 rounded-lg border ${darkMode ? 'border-gray-700' : 'border-slate-200'}`}>
                            <table className="w-full text-sm">
                              <thead className={`sticky top-0 ${darkMode ? 'bg-gray-700' : 'bg-slate-100'}`}>
                                <tr>
                                  <th className={`text-left p-2 ${textClass} w-12`}>#</th>
                                  <th className={`text-left p-2 ${textClass}`}>Aseguradora</th>
                                  <th className={`text-left p-2 ${textClass}`}>Nombre Plan</th>
                                  <th className={`text-left p-2 ${textClass}`}>Descripción</th>
                                </tr>
                              </thead>
                              <tbody>
                                {editablePlanes.map((plan, idx) => (
                                  <tr key={idx} className={`border-t ${darkMode ? 'border-gray-700' : 'border-slate-200'}`}>
                                    <td className={`p-2 ${mutedClass}`}>{idx + 1}</td>
                                    <td className="p-1">
                                      <Select
                                        value={plan.useExistingAseguradora ? plan.existingAseguradoraId : "_new"}
                                        onValueChange={(value) => handlePlanAseguradoraSelect(idx, value)}
                                      >
                                        <SelectTrigger className={`h-8 text-sm w-48 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`} data-testid={`plan-aseg-select-${idx}`}>
                                          <SelectValue placeholder="Seleccionar aseguradora" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="_new">
                                            <span className="flex items-center gap-2">
                                              <Plus className="w-3 h-3" />
                                              Crear nueva: {plan.aseguradora || editableAseguradoras[0]?.nombre || 'Nueva'}
                                            </span>
                                          </SelectItem>
                                          {aseguradoras.map(aseg => (
                                            <SelectItem key={aseg.id} value={aseg.id}>
                                              {aseg.name}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </td>
                                    <td className="p-1">
                                      <Input
                                        value={plan.nombre}
                                        onChange={(e) => handleEditablePlanChange(idx, 'nombre', e.target.value)}
                                        className={`h-8 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                                        placeholder="Nombre del plan"
                                        data-testid={`plan-name-input-${idx}`}
                                      />
                                    </td>
                                    <td className="p-1">
                                      <Input
                                        value={plan.descripcion}
                                        onChange={(e) => handleEditablePlanChange(idx, 'descripcion', e.target.value)}
                                        className={`h-8 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                                        placeholder="Descripción"
                                        data-testid={`plan-desc-input-${idx}`}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Summary */}
                      <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-green-900/20' : 'bg-green-50'} border ${darkMode ? 'border-green-800' : 'border-green-200'}`}>
                        <p className={`text-sm font-medium ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                          Resumen de importación:
                        </p>
                        <ul className={`text-sm ${darkMode ? 'text-green-400' : 'text-green-600'} mt-1 space-y-1`}>
                          <li>• Se crearán <strong>{editableAseguradoras.filter(a => !a.useExisting).length}</strong> aseguradoras</li>
                          <li>• Se crearán <strong>{editablePlanes.length}</strong> planes de cobertura</li>
                          {editablePlanes.length > 0 && (
                            <li>• {editablePlanes.filter(p => p.useExistingAseguradora).length} planes usarán aseguradoras existentes</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              )}

              {/* Step 3: Results */}
              {bulkStep === 3 && bulkResult && (
                <div className="flex flex-col items-center py-8">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
                    (bulkResult.aseguradoras_created > 0 || bulkResult.planes_created > 0) ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
                  }`}>
                    {(bulkResult.aseguradoras_created > 0 || bulkResult.planes_created > 0) ? (
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    ) : (
                      <AlertCircle className="w-10 h-10 text-amber-600" />
                    )}
                  </div>

                  <h3 className={`text-xl font-semibold mb-2 ${textClass}`}>
                    {(bulkResult.aseguradoras_created > 0 || bulkResult.planes_created > 0) ? "Importación Completada" : "Importación con Advertencias"}
                  </h3>
                  
                  <div className="flex gap-8 mb-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-600">{bulkResult.aseguradoras_created}</p>
                      <p className={`text-sm ${mutedClass}`}>Aseguradoras</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-blue-600">{bulkResult.planes_created}</p>
                      <p className={`text-sm ${mutedClass}`}>Planes</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-amber-600">{bulkResult.skipped}</p>
                      <p className={`text-sm ${mutedClass}`}>Omitidos</p>
                    </div>
                  </div>

                  {bulkResult.errors?.length > 0 && (
                    <div className="w-full max-w-lg">
                      <h4 className={`font-medium mb-2 ${mutedClass}`}>Detalles de errores:</h4>
                      <ScrollArea className={`h-32 border rounded-lg p-3 ${darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50'}`}>
                        {bulkResult.errors.map((err, idx) => (
                          <p key={idx} className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'} mb-1`}>{err}</p>
                        ))}
                      </ScrollArea>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className={`border-t ${darkMode ? 'border-gray-700' : ''} pt-4`}>
              {bulkStep === 1 && (
                <Button variant="outline" onClick={() => setShowBulkDialog(false)} className={darkMode ? "border-gray-600" : ""}>
                  Cancelar
                </Button>
              )}
              {bulkStep === 2 && (
                <>
                  <Button variant="outline" onClick={() => setBulkStep(1)} className={darkMode ? "border-gray-600" : ""}>
                    Volver
                  </Button>
                  <Button 
                    onClick={executeBulkImport}
                    disabled={bulkLoading || !Object.values(asegMapping).includes('name')}
                    className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                  >
                    {bulkLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    {bulkLoading ? "Importando..." : `Importar ${bulkPreview?.aseguradoras?.total_rows || 0} Aseguradoras${bulkPreview?.planes ? ` y ${bulkPreview.planes.total_rows || 0} Planes` : ''}`}
                  </Button>
                </>
              )}
              {bulkStep === 3 && (
                <Button onClick={() => setShowBulkDialog(false)} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white">
                  Cerrar
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ========== NUEVO DIÁLOGO: CARGAR PLAN CON IA ========== */}
        <Dialog open={showCargarPlanDialog} onOpenChange={setShowCargarPlanDialog}>
          <DialogContent className={`max-w-5xl max-h-[90vh] flex flex-col ${darkMode ? "bg-gray-800 border-gray-700" : ""}`}>
            <DialogHeader>
              <DialogTitle className={`text-xl font-bold flex items-center gap-2 ${textClass}`}>
                <Brain className="w-5 h-5 text-purple-500" />
                Cargar Plan con IA
              </DialogTitle>
              <DialogDescription className={mutedClass}>
                {cargarPlanStep === 1 && "Sube un documento PDF o Word con la información del plan de cobertura"}
                {cargarPlanStep === 2 && "Revisa y edita los datos extraídos antes de confirmar"}
                {cargarPlanStep === 3 && "Plan cargado exitosamente"}
              </DialogDescription>
            </DialogHeader>

            {/* Progress Steps */}
            <div className={`flex items-center justify-center gap-2 py-3 border-b ${darkMode ? "border-gray-700" : ""}`}>
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${cargarPlanStep >= 1 ? 'bg-purple-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">1</span>
                Subir
              </div>
              <ArrowRight className={`w-4 h-4 ${mutedClass}`} />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${cargarPlanStep >= 2 ? 'bg-purple-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">2</span>
                Revisar
              </div>
              <ArrowRight className={`w-4 h-4 ${mutedClass}`} />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${cargarPlanStep >= 3 ? 'bg-purple-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">3</span>
                Listo
              </div>
            </div>

            <div className="flex-1 overflow-auto py-4">
              {/* Step 1: Upload */}
              {cargarPlanStep === 1 && (
                <div 
                  className={`flex flex-col items-center justify-center py-12 mx-4 border-2 border-dashed rounded-xl transition-all duration-200 ${
                    cargarPlanDragging 
                      ? 'border-purple-500 bg-purple-500/10 scale-[1.02]' 
                      : darkMode 
                        ? 'border-gray-600 hover:border-purple-500' 
                        : 'border-slate-300 hover:border-purple-500'
                  }`}
                  onDragOver={handleCargarPlanDragOver}
                  onDragLeave={handleCargarPlanDragLeave}
                  onDrop={handleCargarPlanDrop}
                >
                  <input
                    type="file"
                    accept=".pdf,.docx,.doc"
                    onChange={handleCargarPlanFileSelect}
                    className="hidden"
                    id="cargar-plan-file"
                  />
                  
                  {cargarPlanLoading ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-16 h-16 animate-spin text-purple-500 mx-auto mb-4" />
                      <p className={`text-lg font-medium ${textClass}`}>Analizando documento con IA...</p>
                      <p className={`text-sm ${mutedClass} mt-2`}>Extrayendo información del plan de cobertura</p>
                    </div>
                  ) : cargarPlanDragging ? (
                    <div className="text-center py-8">
                      <div className="w-24 h-24 rounded-full bg-purple-500/20 flex items-center justify-center mb-4 mx-auto animate-pulse">
                        <Upload className="w-12 h-12 text-purple-500" />
                      </div>
                      <p className="text-lg font-medium text-purple-500">Suelte el archivo aquí</p>
                    </div>
                  ) : (
                    <>
                      <div className="w-24 h-24 rounded-full bg-purple-500/10 flex items-center justify-center mb-6">
                        <Brain className="w-12 h-12 text-purple-500" />
                      </div>
                      
                      <p className={`text-xl font-medium mb-2 ${textClass}`}>
                        Arrastra y suelta tu documento aquí
                      </p>
                      <p className={`text-sm ${mutedClass} mb-6`}>o</p>
                      
                      <label htmlFor="cargar-plan-file">
                        <Button 
                          className="bg-purple-500 hover:bg-purple-600 text-white mb-8 cursor-pointer"
                          asChild
                        >
                          <span>
                            <Upload className="w-4 h-4 mr-2" />
                            Seleccionar Archivo
                          </span>
                        </Button>
                      </label>
                      
                      <div className={`text-sm ${mutedClass} text-center max-w-lg space-y-3`}>
                        <div className={`${darkMode ? 'bg-gray-700' : 'bg-slate-50'} rounded-lg p-4 text-left`}>
                          <p className="font-semibold mb-2">Formatos soportados:</p>
                          <p>• <strong>PDF (.pdf):</strong> Cuadros de cobertura, pólizas</p>
                          <p>• <strong>Word (.docx, .doc):</strong> Documentos de planes</p>
                        </div>
                        <div className={`${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'} rounded-lg p-4 text-left`}>
                          <p className="font-semibold mb-1 text-purple-700 dark:text-purple-300">¿Qué hace la IA?</p>
                          <p className="text-purple-600 dark:text-purple-400 text-xs">
                            Analiza el documento y extrae automáticamente: nombre del plan, coberturas, 
                            límites, deducibles, copagos, primas, red médica, exclusiones y más.
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Step 2: Preview and Edit */}
              {cargarPlanStep === 2 && extractedPlanData && (
                <ScrollArea className="h-[60vh] pr-4">
                  <div className="space-y-6 px-2">
                    {/* Aseguradora y Categoría */}
                    <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-blue-50 border-blue-200'}`}>
                      <h3 className={`font-semibold mb-4 ${textClass} flex items-center gap-2`}>
                        <Building2 className="w-4 h-4" />
                        Aseguradora y Categoría
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className={mutedClass}>
                            Aseguradora *
                            {extractedPlanData.aseguradora_detectada && (
                              <span className="ml-2 text-xs text-purple-500">
                                (Detectada: {extractedPlanData.aseguradora_detectada})
                              </span>
                            )}
                          </Label>
                          <Select value={selectedAseguradoraId} onValueChange={setSelectedAseguradoraId}>
                            <SelectTrigger className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} data-testid="cargar-plan-aseg-select">
                              <SelectValue placeholder="Seleccionar aseguradora" />
                            </SelectTrigger>
                            <SelectContent>
                              {aseguradoras.map(aseg => (
                                <SelectItem key={aseg.id} value={aseg.id}>{aseg.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Categoría del Plan</Label>
                          <Select value={selectedCategoria} onValueChange={setSelectedCategoria}>
                            <SelectTrigger className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""} data-testid="cargar-plan-cat-select">
                              <SelectValue placeholder="Seleccionar categoría" />
                            </SelectTrigger>
                            <SelectContent>
                              {CATEGORIES.map(cat => (
                                <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Información Básica */}
                    <div className="space-y-4">
                      <h3 className={`font-semibold border-b pb-2 ${textClass}`}>Información Básica</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className={mutedClass}>Nombre del Plan *</Label>
                          <Input 
                            value={extractedPlanData.name || ""} 
                            onChange={(e) => handleExtractedPlanChange('name', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                            data-testid="cargar-plan-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Tipo de Plan</Label>
                          <Input 
                            value={extractedPlanData.tipo_plan || ""} 
                            onChange={(e) => handleExtractedPlanChange('tipo_plan', e.target.value)}
                            placeholder="Individual/Familiar/Colectivo"
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className={mutedClass}>Descripción</Label>
                        <Textarea 
                          value={extractedPlanData.description || ""} 
                          onChange={(e) => handleExtractedPlanChange('description', e.target.value)}
                          rows={2}
                          className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className={mutedClass}>Vigencia</Label>
                          <Input 
                            value={extractedPlanData.vigencia || ""} 
                            onChange={(e) => handleExtractedPlanChange('vigencia', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Moneda</Label>
                          <Select value={extractedPlanData.moneda || "USD"} onValueChange={(v) => handleExtractedPlanChange('moneda', v)}>
                            <SelectTrigger className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="USD">USD</SelectItem>
                              <SelectItem value="DOP">DOP (RD$)</SelectItem>
                              <SelectItem value="EUR">EUR</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Edad Mínima/Máxima</Label>
                          <Input 
                            value={`${extractedPlanData.edad_minima || ''} - ${extractedPlanData.edad_maxima || ''}`} 
                            onChange={(e) => {
                              const parts = e.target.value.split('-').map(p => p.trim());
                              handleExtractedPlanChange('edad_minima', parts[0] || '');
                              handleExtractedPlanChange('edad_maxima', parts[1] || '');
                            }}
                            placeholder="0 - 65"
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Coberturas */}
                    <div className="space-y-4">
                      <h3 className={`font-semibold border-b pb-2 ${textClass}`}>Coberturas</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className={mutedClass}>Hospitalización</Label>
                          <Textarea 
                            value={extractedPlanData.cobertura_hospitalizacion || ""} 
                            onChange={(e) => handleExtractedPlanChange('cobertura_hospitalizacion', e.target.value)}
                            rows={2}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Cirugía</Label>
                          <Textarea 
                            value={extractedPlanData.cobertura_cirugia || ""} 
                            onChange={(e) => handleExtractedPlanChange('cobertura_cirugia', e.target.value)}
                            rows={2}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Maternidad</Label>
                          <Textarea 
                            value={extractedPlanData.cobertura_maternidad || ""} 
                            onChange={(e) => handleExtractedPlanChange('cobertura_maternidad', e.target.value)}
                            rows={2}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Emergencia</Label>
                          <Textarea 
                            value={extractedPlanData.cobertura_emergencia || ""} 
                            onChange={(e) => handleExtractedPlanChange('cobertura_emergencia', e.target.value)}
                            rows={2}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Medicamentos</Label>
                          <Textarea 
                            value={extractedPlanData.cobertura_medicamentos || ""} 
                            onChange={(e) => handleExtractedPlanChange('cobertura_medicamentos', e.target.value)}
                            rows={2}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Vida</Label>
                          <Textarea 
                            value={extractedPlanData.cobertura_vida || ""} 
                            onChange={(e) => handleExtractedPlanChange('cobertura_vida', e.target.value)}
                            rows={2}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Accidentes</Label>
                          <Textarea 
                            value={extractedPlanData.cobertura_accidentes || ""} 
                            onChange={(e) => handleExtractedPlanChange('cobertura_accidentes', e.target.value)}
                            rows={2}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Enfermedades Graves</Label>
                          <Textarea 
                            value={extractedPlanData.cobertura_enfermedades_graves || ""} 
                            onChange={(e) => handleExtractedPlanChange('cobertura_enfermedades_graves', e.target.value)}
                            rows={2}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Dental</Label>
                          <Input 
                            value={extractedPlanData.cobertura_dental || ""} 
                            onChange={(e) => handleExtractedPlanChange('cobertura_dental', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Visión</Label>
                          <Input 
                            value={extractedPlanData.cobertura_vision || ""} 
                            onChange={(e) => handleExtractedPlanChange('cobertura_vision', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Límites y Deducibles */}
                    <div className="space-y-4">
                      <h3 className={`font-semibold border-b pb-2 ${textClass}`}>Límites y Deducibles</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className={mutedClass}>Suma Asegurada Máxima</Label>
                          <Input 
                            value={extractedPlanData.suma_asegurada_maxima || ""} 
                            onChange={(e) => handleExtractedPlanChange('suma_asegurada_maxima', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Límite Anual</Label>
                          <Input 
                            value={extractedPlanData.limite_anual || ""} 
                            onChange={(e) => handleExtractedPlanChange('limite_anual', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Deducible General</Label>
                          <Input 
                            value={extractedPlanData.deducible_general || ""} 
                            onChange={(e) => handleExtractedPlanChange('deducible_general', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Copagos */}
                    <div className="space-y-4">
                      <h3 className={`font-semibold border-b pb-2 ${textClass}`}>Copagos</h3>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label className={mutedClass}>Consulta</Label>
                          <Input 
                            value={extractedPlanData.copago_consulta || ""} 
                            onChange={(e) => handleExtractedPlanChange('copago_consulta', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Especialista</Label>
                          <Input 
                            value={extractedPlanData.copago_especialista || ""} 
                            onChange={(e) => handleExtractedPlanChange('copago_especialista', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Emergencia</Label>
                          <Input 
                            value={extractedPlanData.copago_emergencia || ""} 
                            onChange={(e) => handleExtractedPlanChange('copago_emergencia', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Medicamentos</Label>
                          <Input 
                            value={extractedPlanData.copago_medicamentos || ""} 
                            onChange={(e) => handleExtractedPlanChange('copago_medicamentos', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Períodos de Espera */}
                    <div className="space-y-4">
                      <h3 className={`font-semibold border-b pb-2 ${textClass}`}>Períodos de Espera</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className={mutedClass}>Espera General</Label>
                          <Input 
                            value={extractedPlanData.espera_general || ""} 
                            onChange={(e) => handleExtractedPlanChange('espera_general', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Espera Maternidad</Label>
                          <Input 
                            value={extractedPlanData.espera_maternidad || ""} 
                            onChange={(e) => handleExtractedPlanChange('espera_maternidad', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Espera Preexistencias</Label>
                          <Input 
                            value={extractedPlanData.espera_preexistencias || ""} 
                            onChange={(e) => handleExtractedPlanChange('espera_preexistencias', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Primas */}
                    <div className="space-y-4">
                      <h3 className={`font-semibold border-b pb-2 ${textClass}`}>Primas</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className={mutedClass}>Prima Mensual</Label>
                          <Input 
                            value={extractedPlanData.prima_mensual || ""} 
                            onChange={(e) => handleExtractedPlanChange('prima_mensual', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Prima Anual</Label>
                          <Input 
                            value={extractedPlanData.prima_anual || ""} 
                            onChange={(e) => handleExtractedPlanChange('prima_anual', e.target.value)}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Información Adicional */}
                    <div className="space-y-4">
                      <h3 className={`font-semibold border-b pb-2 ${textClass}`}>Información Adicional</h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className={mutedClass}>Red Médica / Hospitales</Label>
                          <Textarea 
                            value={extractedPlanData.red_medica || ""} 
                            onChange={(e) => handleExtractedPlanChange('red_medica', e.target.value)}
                            rows={2}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Cobertura Internacional</Label>
                          <Textarea 
                            value={extractedPlanData.cobertura_internacional || ""} 
                            onChange={(e) => handleExtractedPlanChange('cobertura_internacional', e.target.value)}
                            rows={2}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Exclusiones</Label>
                          <Textarea 
                            value={extractedPlanData.exclusiones || ""} 
                            onChange={(e) => handleExtractedPlanChange('exclusiones', e.target.value)}
                            rows={3}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Beneficios Adicionales</Label>
                          <Textarea 
                            value={extractedPlanData.beneficios_adicionales || ""} 
                            onChange={(e) => handleExtractedPlanChange('beneficios_adicionales', e.target.value)}
                            rows={2}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={mutedClass}>Notas Importantes</Label>
                          <Textarea 
                            value={extractedPlanData.notas_importantes || ""} 
                            onChange={(e) => handleExtractedPlanChange('notas_importantes', e.target.value)}
                            rows={2}
                            className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              )}

              {/* Step 3: Success */}
              {cargarPlanStep === 3 && cargarPlanResult && (
                <div className="flex flex-col items-center py-12">
                  <div className="w-24 h-24 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-6">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                  <h3 className={`text-2xl font-semibold mb-2 ${textClass}`}>
                    ¡Plan Cargado Exitosamente!
                  </h3>
                  <p className={`text-center ${mutedClass} mb-6`}>
                    {cargarPlanResult.message}
                  </p>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-slate-100'} text-center`}>
                    <p className={`text-sm ${mutedClass}`}>ID del Plan:</p>
                    <p className={`font-mono text-sm ${textClass}`}>{cargarPlanResult.plan_id}</p>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className={`border-t pt-4 ${darkMode ? "border-gray-700" : ""}`}>
              {cargarPlanStep === 1 && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowCargarPlanDialog(false)}
                  className={darkMode ? "border-gray-600" : ""}
                >
                  Cancelar
                </Button>
              )}
              {cargarPlanStep === 2 && (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => setCargarPlanStep(1)}
                    className={darkMode ? "border-gray-600" : ""}
                  >
                    Volver
                  </Button>
                  <Button 
                    onClick={confirmarCargaPlan}
                    disabled={cargarPlanLoading || !selectedAseguradoraId || !extractedPlanData?.name}
                    className="bg-purple-500 hover:bg-purple-600 text-white"
                    data-testid="confirm-cargar-plan"
                  >
                    {cargarPlanLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Confirmar y Guardar
                  </Button>
                </>
              )}
              {cargarPlanStep === 3 && (
                <Button 
                  onClick={() => setShowCargarPlanDialog(false)} 
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                >
                  Cerrar
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
